import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import InputAdornment from '@mui/material/InputAdornment'
import Skeleton from '@mui/material/Skeleton'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded'
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded'
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import PollCheckDecoration from './PollCheckDecoration'
import {
  altColorDarkText,
  altColorLighter,
  borderColor,
  headingFont,
  helperTextColor,
  mainColorDarker,
  mainColorLighter,
  shadow1,
} from 'src/ui/theme/tokens'

export function DiscoveryHeading({
  poll,
  title,
  description,
}: {
  poll?: boolean
  title: string
  description: string
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Box
        component="img"
        src={poll ? '/brand/random-poll.svg' : '/brand/podium.svg'}
        alt=""
        aria-hidden
        sx={{
          width: { xs: 56, sm: 72 },
          height: 'auto',
          flexShrink: 0,
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h4"
          textAlign="left"
          sx={{
            fontSize: { xs: 24, sm: 28 },
            fontWeight: 600,
            color: poll ? '#693413' : '#ffffff',
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color={poll ? '#855c3d' : '#b9d4e5'} textAlign="left">
          {description}
        </Typography>
      </Box>
    </Box>
  )
}

export function DiscoveryFilters({
  heading,
  poll,
  query,
  onQuery,
  categories,
  categoryOptions,
  onCategories,
  tags,
  tagOptions,
  onTags,
  filters,
  onFilter,
  onRemove,
}: {
  heading?: React.ReactNode
  poll?: boolean
  query: string
  onQuery: (value: string) => void
  categories: string[]
  categoryOptions: string[]
  onCategories: (value: string[]) => void
  tags: string[]
  tagOptions: string[]
  onTags: (value: string[]) => void
  filters: { label: string; value: string; selected: boolean }[]
  onFilter: (value: string) => void
  onRemove?: () => void
}) {
  return (
    <Box
      sx={{
        border: `1px solid ${poll ? '#efddc9' : '#335770'}`,
        borderRadius: '20px',
        p: { xs: 1.5, sm: 2 },
        background: poll ? '#fff3e5' : '#193b58',
        boxShadow: poll ? '0 4px 0 #e8bb99' : '0 4px 0 #bfd4e3',
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        '& > :not(.rt-poll-decoration)': { position: 'relative', zIndex: 1 },
        '& .MuiTextField-root .MuiOutlinedInput-root:not(.Mui-disabled)': {
          background: poll ? '#fff7ed' : '#315570',
          color: poll ? '#693413' : '#ffffff',
          '& fieldset': { borderColor: poll ? '#e8cdb2' : '#587991' },
          '&:hover fieldset': { borderColor: poll ? '#e98549' : '#86cde5' },
          '&.Mui-focused fieldset': { borderColor: poll ? '#e98549' : '#53d9f5' },
          '& input::placeholder': { color: poll ? '#855c3d' : '#c0d8e8', opacity: 1 },
          '& .MuiSvgIcon-root': { color: poll ? '#855c3d' : '#c0d8e8' },
          '& .MuiChip-root': { color: '#193b58' },
        },
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          pointerEvents: 'none',
          zIndex: 0,
        },
        '&::before': poll
          ? { display: 'none' }
          : {
              width: 200,
              height: 200,
              right: -55,
              top: -75,
              border: '28px solid #53d9f510',
              borderRadius: '50%',
            },
        '&::after': poll
          ? { display: 'none' }
          : {
              width: 75,
              height: 75,
              left: -35,
              bottom: -45,
              border: '14px solid #53d9f50c',
              borderRadius: '50%',
            },
        mb: 2,
      }}
    >
      {poll && <PollCheckDecoration />}
      {heading}
      <TextField
        fullWidth
        size="small"
        placeholder={poll ? 'Find your next community poll…' : 'What do you want to rank?'}
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        inputProps={{ 'aria-label': poll ? 'Search polls' : 'Search trivia' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon sx={{ color: helperTextColor }} />
            </InputAdornment>
          ),
        }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 1,
          mt: 1,
        }}
      >
        {[
          {
            name: 'Categories',
            options: categoryOptions,
            value: categories,
            change: onCategories,
          },
          { name: 'Tags', options: tagOptions, value: tags, change: onTags },
        ].map(({ name, options, value, change }) => (
          <Autocomplete
            key={name}
            multiple
            size="small"
            options={options}
            value={value}
            onChange={(_e, next, reason) => {
              if (reason === 'removeOption' || reason === 'clear') onRemove?.()
              change(next)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={value.length ? undefined : name}
                inputProps={{ ...params.inputProps, 'aria-label': name }}
              />
            )}
            renderTags={(values, getTagProps) =>
              values.map((label, index) => {
                const { key, ...props } = getTagProps({ index })
                return (
                  <Chip
                    key={key}
                    {...props}
                    size="small"
                    label={label}
                    sx={{
                      maxWidth: '100%',
                      background: name === 'Tags' ? altColorLighter : mainColorLighter,
                    }}
                  />
                )
              })
            }
          />
        ))}
      </Box>
      <Box aria-label="Browse filters" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
        {filters.map((filter) => (
          <Button
            key={filter.value}
            size="small"
            variant="text"
            aria-pressed={filter.selected}
            onClick={() => onFilter(filter.value)}
            sx={{
              borderRadius: '13px',
              px: 1.25,
              minWidth: 0,
              minHeight: 36,
              fontSize: 12,
              color: filter.selected
                ? poll
                  ? altColorDarkText
                  : mainColorDarker
                : poll
                  ? '#855c3d'
                  : '#c0d8e8',
              background: filter.selected
                ? poll
                  ? altColorLighter
                  : mainColorLighter
                : 'transparent',
              fontWeight: filter.selected ? 800 : 600,
              '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                  background: poll ? altColorLighter : mainColorLighter,
                },
              },
            }}
          >
            {filter.label}
          </Button>
        ))}
      </Box>
    </Box>
  )
}

export function DiscoveryCard({
  poll,
  kindLabel,
  title,
  badge,
  metadata,
  plays,
  votes,
  rateUps,
  rateDowns,
  children,
}: {
  poll?: boolean
  kindLabel?: string
  title: React.ReactNode
  badge?: string
  metadata: React.ReactNode
  plays: number
  votes?: number
  rateUps: number
  rateDowns: number
  children: React.ReactNode
}) {
  return (
    <Box
      component="article"
      data-testid="discovery-card"
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        border: `1px solid ${borderColor}`,
        borderRadius: '20px',
        p: { xs: 1.5, sm: 2 },
        boxShadow: shadow1,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 0.5,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: poll ? altColorDarkText : mainColorDarker,
            fontSize: 10,
            letterSpacing: 1.1,
            fontWeight: 800,
            lineHeight: 1.4,
          }}
        >
          {kindLabel || (poll ? 'COMMUNITY POLL' : 'TRIVIA · 10 ITEMS')}
        </Typography>
        {badge && (
          <Chip size="small" label={badge} sx={{ fontSize: 10, height: 24, maxWidth: '55%' }} />
        )}
      </Box>
      <Typography
        component="h3"
        textAlign="left"
        sx={{
          fontFamily: headingFont,
          fontSize: { xs: 18, sm: 20 },
          fontWeight: 800,
          letterSpacing: '-0.015em',
          lineHeight: 1.2,
          overflowWrap: 'anywhere',
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
          minWidth: 0,
          mb: 1,
          '& .MuiChip-root': { height: 26 },
          '& .MuiChip-label': { px: 1 },
        }}
      >
        {metadata}
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: 1,
            rowGap: 0.5,
            color: helperTextColor,
            fontSize: 11,
            mb: 1,
          }}
        >
          {votes !== undefined && <Box>{votes.toLocaleString()} votes</Box>}
          <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
            <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />
            {plays.toLocaleString()} plays
          </Box>
          <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
            <ThumbUpRoundedIcon sx={{ fontSize: 13 }} />
            {rateUps}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
            <ThumbDownRoundedIcon sx={{ fontSize: 13 }} />
            {rateDowns}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            '& > button': { flex: 1, minHeight: 44, py: 0.75, borderRadius: '14px' },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export function DiscoveryLoading() {
  return (
    <Box
      aria-label="Loading results"
      aria-busy="true"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5,
      }}
    >
      {Array.from({ length: 4 }, (_, i) => (
        <Box
          key={i}
          sx={{
            border: `1px solid ${borderColor}`,
            borderRadius: '20px',
            p: { xs: 1.5, sm: 2 },
            background: '#fff',
          }}
        >
          <Skeleton height={14} width="35%" />
          <Skeleton height={28} width="85%" sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={26} width="75%" sx={{ mb: 1 }} />
          <Skeleton height={18} width="65%" sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={44} />
        </Box>
      ))}
    </Box>
  )
}

export function DiscoveryEmpty({ onClear }: { onClear: () => void }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        border: `1px dashed ${borderColor}`,
        borderRadius: '24px',
        py: 7,
        px: 2,
        background: '#fff',
        minHeight: 320,
      }}
    >
      <SearchRoundedIcon sx={{ fontSize: 42, color: helperTextColor, mb: 1 }} />
      <Typography variant="h5" fontWeight={800}>
        No results
      </Typography>
      <Typography color={helperTextColor} variant="body2" sx={{ my: 1 }}>
        Try a different topic or clear your filters.
      </Typography>
      <Button onClick={onClear} variant="outlined">
        Clear
      </Button>
    </Box>
  )
}

export function DiscoveryPagination({
  page,
  total,
  limit,
  onPage,
}: {
  page: number
  total: number
  limit: number
  onPage: (page: number) => void
}) {
  const count = Math.ceil(total / limit)
  if (!count) return null
  const current = page + 1
  const candidates =
    count <= 5
      ? Array.from({ length: count }, (_, i) => i + 1)
      : current <= 3
        ? [1, 2, 3, 4, count]
        : current >= count - 2
          ? [1, count - 3, count - 2, count - 1, count]
          : [1, current - 1, current, current + 1, count]
  return (
    <Box
      component="nav"
      aria-label="Results pages"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 0.25, sm: 0.75 },
        flexWrap: 'wrap',
        mt: 3,
        pb: 2,
      }}
    >
      <Button
        aria-label="Previous page"
        disabled={page === 0}
        onClick={() => onPage(page - 1)}
        sx={{ minWidth: 32, px: 0 }}
      >
        <NavigateBeforeRoundedIcon />
      </Button>
      {candidates.map((number, index) => (
        <React.Fragment key={number}>
          {index > 0 && number - candidates[index - 1]! > 1 && (
            <Typography color={helperTextColor} fontSize={12}>
              …
            </Typography>
          )}
          <Button
            aria-label={`Page ${number}`}
            aria-current={number === current ? 'page' : undefined}
            onClick={() => onPage(number - 1)}
            sx={{
              minWidth: 30,
              px: 0.5,
              borderRadius: '10px',
              background: number === current ? mainColorLighter : 'transparent',
              fontWeight: 800,
            }}
          >
            {number}
          </Button>
        </React.Fragment>
      ))}
      <Button
        aria-label="Next page"
        disabled={page >= count - 1}
        onClick={() => onPage(page + 1)}
        sx={{ minWidth: 32, px: 0 }}
      >
        <NavigateNextRoundedIcon />
      </Button>
      <Typography
        variant="caption"
        color={helperTextColor}
        sx={{ textAlign: 'center', width: '100%', mt: 1 }}
      >
        {total.toLocaleString()} results · {count.toLocaleString()} {count === 1 ? 'page' : 'pages'}
      </Typography>
    </Box>
  )
}
